import type { ExamDetailQuestion, Question } from "@/types";
import CategoryBadge from "./CategoryBadge";
import MediaDisplay from "./MediaDisplay";
import AnswerOption from "./AnswerOption";

interface QuestionCardProps {
  question: Question | ExamDetailQuestion;
  questionNumber: number;
  totalQuestions: number;
  selectedAnswerId?: string;
  showResult?: boolean;
  onSelectAnswer: (answerId: string) => void;
}

const ANSWER_LABELS = ["A", "B", "C", "D", "E", "F"];

export default function QuestionCard({
  question,
  questionNumber,
  totalQuestions,
  selectedAnswerId,
  showResult,
  onSelectAnswer,
}: QuestionCardProps) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium text-gray-500">
          Question {questionNumber} of {totalQuestions}
        </span>
        {"category" in question && question.category && (
          <CategoryBadge name={question.category.name} />
        )}
      </div>

      <h2 className="text-lg font-semibold text-gray-900 leading-relaxed">
        {question.text}
      </h2>

      <MediaDisplay
        imageUrl={question.image_url}
        videoUrl={question.video_url}
        hasImage={question.has_image}
        hasVideo={question.has_video}
      />

      <div className="mt-6 space-y-3">
        {question.answers.map((answer, index) => (
          <AnswerOption
            key={answer.id}
            id={answer.id}
            text={answer.text}
            label={ANSWER_LABELS[index] || String(index + 1)}
            isSelected={selectedAnswerId === answer.id}
            isCorrect={showResult ? answer.is_correct : undefined}
            isIncorrect={showResult ? !answer.is_correct : undefined}
            showResult={showResult}
            disabled={showResult}
            onSelect={onSelectAnswer}
          />
        ))}
      </div>

      {showResult && (
        <div className="mt-6 rounded-lg bg-primary-50 border border-primary-200 p-4">
          <h4 className="text-sm font-semibold text-primary-800 mb-1">Explanation</h4>
          <p className="text-sm text-primary-700">{question.explanation}</p>
        </div>
      )}
    </div>
  );
}
